// DrawerSidebar.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Chip,
  CircularProgress,
} from "@mui/material";
import { renderPlatformIconCompact } from "./PlatformIconCompact";
import DateRangePicker from "./DateRangePicker";
import DateTimeRangePicker from "./DateTimeRangePicker";
import { modelsApi, reportsApi } from "../../../../../API/axiosInstance";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const DrawerSidebar = ({
  topicData,
  loading,
  totalPosts,
  feedData,
  dateRange,
  platformsInfo = [],
  sortOption,
  selectedPlatforms,
  setSelectedPlatforms,
  searchQuery,
  setSearchQuery,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  onApplyFilters,
  onClearFilters,
  onSortChange,
  // Reports-related props (lifted to parent)
  activeTab,
  onTabChange,
  selectedBatch,
  onSelectBatch,
  recordsMinDate,
  recordsMaxDate,
}) => {
  const [analysisFromDate, setAnalysisFromDate] = useState("");
  const [analysisToDate, setAnalysisToDate] = useState("");
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const [selectedFormat, setSelectedFormat] = useState("pdf");
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMode, setReportMode] = useState("batch"); // "batch" | "custom"
  const [customStartDate, setCustomStartDate] = useState(""); // ISO datetime string
  const [customEndDate, setCustomEndDate] = useState("");     // ISO datetime string

  // Auto-switch to batch mode when a batch is selected from the left panel
  useEffect(() => {
    if (selectedBatch) setReportMode("batch");
  }, [selectedBatch]);

  const rangeMinDate = dateRange?.from ? dayjs(dateRange.from) : undefined;
  const rangeMaxDate = dateRange?.to ? dayjs(dateRange.to) : undefined;

  const handleInitiateAnalysis = async () => {
    if (!analysisFromDate || !analysisToDate) {
      toast.warn("Please select both start and end dates.");
      return;
    }
    if (!topicData?._id) {
      toast.error("Topic ID not found.");
      return;
    }

    setAnalysisLoading(true);
    try {
      const payload = {
        topic_id: topicData._id,
        start_date: `${analysisFromDate}T00:00:00`,
        end_date: `${analysisToDate}T23:59:59`,
      };
      await modelsApi.post(
        `/topic/topic-pipeline`,
        payload
      );
      toast.success("Analysis initiated successfully!");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to initiate analysis."
      );
    } finally {
      setAnalysisLoading(false);
    }
  };


  const handleGenerateReport = async () => {
    if (!topicData?._id) {
      toast.error("Topic ID not found.");
      return;
    }

    let startDate, endDate;

    if (reportMode === "batch") {
      if (!selectedBatch) {
        toast.warn("Please select a Records from the left panel.");
        return;
      }
      startDate = selectedBatch.start_date;
      endDate = selectedBatch.end_date;
    } else {
      if (!customStartDate || !customEndDate) {
        toast.warn("Please select both start and end date & time.");
        return;
      }
      startDate = customStartDate;
      endDate = customEndDate;
    }

    setReportLoading(true);
    try {
      const payload = {
        topicId: topicData._id,
        topic_name: topicData.topic,
        topic_type: topicData.type,
        filetype: selectedFormat === "xlsx" ? "excel" : selectedFormat,
        start_date: startDate,
        end_date: endDate,
      };

      const response = await reportsApi.post(
        `/topic`,
        payload,
        { responseType: "blob" }
      );

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers["content-disposition"];
      let filename = `report.${selectedFormat}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/);
        if (match?.[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Report downloaded successfully!");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to generate report."
      );
    } finally {
      setReportLoading(false);
    }
  };

  const formats = [
    {
      value: "xlsx",
      label: "Excel",
      subtitle: ".xlsx",
      icon: "📊",
      color: "#16a34a",
      bgColor: "#f0fdf4",
    },
    {
      value: "pdf",
      label: "PDF",
      subtitle: ".pdf",
      icon: "📕",
      color: "#dc2626",
      bgColor: "#fef2f2",
    },
  ];

  return (
    <Box
      sx={{
        flex: "0 0 25%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "rgba(248, 250, 252, 0.9)",
        borderLeft: "1px solid #e2e8f0",
      }}
    >
      {/* Tabs */}
      <Box
        sx={{ borderBottom: "1px solid #e2e8f0", backgroundColor: "#ffffff" }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => onTabChange(newValue)}
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "#0f766e",
              height: 3,
            },
            "& .MuiTab-root": {
              fontFamily: "Host Grotesk, sans-serif",
              fontWeight: 600,
              fontSize: "0.8rem",
              textTransform: "none",
              color: "#64748b",
              minWidth: 0,
              px: 1.5,
              "&.Mui-selected": {
                color: "#0f766e",
              },
            },
          }}
        >
          <Tab label="Data Analytics" />
          <Tab label="Filters" />
          <Tab label="Reports" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        {/* Data Analytics Tab */}
        {activeTab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Objective & Status */}
            <Box
              sx={{
                px: 2,
                py: 1,
                backgroundColor: "#fff",
                borderRadius: "6px",
                border: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontFamily: "Host Grotesk, sans-serif",
                  fontSize: "0.75rem",
                }}
              >
                Objective:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#3b82f6",
                  fontFamily: "Host Grotesk, sans-serif",
                  fontSize: "0.85rem",
                }}
              >
                {`${topicData?.type
                  ? topicData.type.charAt(0).toUpperCase() +
                  topicData.type.slice(1)
                  : ""
                  } Analysis`}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontFamily: "Host Grotesk, sans-serif",
                  fontSize: "0.75rem",
                }}
              >
                Status:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#3b82f6",
                  fontFamily: "Host Grotesk, sans-serif",
                  fontSize: "0.85rem",
                }}
              >
                {topicData?.status
                  ? topicData.status.charAt(0).toUpperCase() +
                  topicData.status.slice(1)
                  : ""}
              </Typography>
            </Box>

            {/* Initiate Analysis */}
            <Box
              sx={{
                p: 2,
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#334155",
                  fontFamily: "Host Grotesk, sans-serif",
                }}
              >
                Initiate Analysis
              </Typography>

              <DateRangePicker
                fromDate={analysisFromDate}
                setFromDate={setAnalysisFromDate}
                toDate={analysisToDate}
                setToDate={setAnalysisToDate}
                minDate={rangeMinDate}
                maxDate={rangeMaxDate}
              />

              <Button
                variant="contained"
                onClick={handleInitiateAnalysis}
                disabled={analysisLoading}
                startIcon={
                  analysisLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
                sx={{
                  backgroundColor: "#0f766e",
                  color: "white",
                  fontFamily: "Host Grotesk, sans-serif",
                  fontWeight: 600,
                  textTransform: "none",
                  py: 1.5,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(15, 118, 110, 0.2)",
                  "&:hover": {
                    backgroundColor: "#0d5b57",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(15, 118, 110, 0.3)",
                  },
                  "&:disabled": {
                    backgroundColor: "#94a3b8",
                    color: "white",
                    transform: "none",
                    boxShadow: "none",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {analysisLoading ? "Initiating..." : "Initiate Analysis"}
              </Button>
            </Box>

            {/* Total Posts */}
            <Box
              sx={{
                p: 2,
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "700",
                  color: "#0f766e",
                  mb: 1,
                  fontFamily: "Host Grotesk, sans-serif",
                }}
              >
                {loading ? "--" : totalPosts || 0}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontFamily: "Host Grotesk, sans-serif",
                }}
              >
                Total Posts
              </Typography>
            </Box>

            {/* Data Range Indicator */}
            {dateRange?.from && dateRange?.to && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: "#fff",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    fontFamily: "Host Grotesk, sans-serif",
                    fontSize: "0.72rem",
                  }}
                >
                  Data Available
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#0f766e",
                      fontFamily: "Host Grotesk, sans-serif",
                      fontSize: "0.8rem",
                    }}
                  >
                    {dayjs(dateRange.from).format("DD MMM YYYY")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#94a3b8", fontFamily: "Host Grotesk, sans-serif" }}
                  >
                    →
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#0f766e",
                      fontFamily: "Host Grotesk, sans-serif",
                      fontSize: "0.8rem",
                    }}
                  >
                    {dayjs(dateRange.to).format("DD MMM YYYY")}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Platforms */}
            {topicData?.platform?.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontFamily: "Host Grotesk, sans-serif",
                    mb: 1,
                    textAlign: "center",
                  }}
                >
                  Platforms
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    justifyContent: "center",
                  }}
                >
                  {topicData.platform.map((platform, index) => (
                    <React.Fragment key={`${platform}-${index}`}>
                      {renderPlatformIconCompact(platform)}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Filters Tab */}
        {activeTab === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Date Range */}
            <DateRangePicker
              fromDate={fromDate}
              setFromDate={setFromDate}
              toDate={toDate}
              setToDate={setToDate}
              minDate={rangeMinDate}
              maxDate={rangeMaxDate}
            />

            {/* Sort By */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "600",
                  color: "#334155",
                  mb: 1,
                  fontFamily: "Host Grotesk, sans-serif",
                }}
              >
                Sort By
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel
                  sx={{
                    fontFamily: "Host Grotesk, sans-serif",
                    "&.Mui-focused": { color: "#0f766e" },
                  }}
                >
                  Sort by
                </InputLabel>
                <Select
                  value={sortOption}
                  onChange={(e) => onSortChange(e.target.value)}
                  label="Sort by"
                  sx={{
                    fontFamily: "Host Grotesk, sans-serif",
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0f766e",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0f766e",
                    },
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="likes">Likes</MenuItem>
                  <MenuItem value="reach">Reach</MenuItem>
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                  <MenuItem value="media">Media Present</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Platform Filter */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "600",
                  color: "#334155",
                  mb: 1,
                  fontFamily: "Host Grotesk, sans-serif",
                }}
              >
                Filter by Platform
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel
                  sx={{
                    fontFamily: "Host Grotesk, sans-serif",
                    "&.Mui-focused": { color: "#0f766e" },
                  }}
                >
                  Select Platforms
                </InputLabel>
                <Select
                  multiple
                  value={selectedPlatforms}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedPlatforms(
                      typeof value === "string" ? value.split(",") : value
                    );
                  }}
                  input={<OutlinedInput label="Select Platforms" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          sx={{
                            background: "#0f766e",
                            color: "white",
                            fontWeight: 500,
                            fontSize: "0.7rem",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  sx={{
                    fontFamily: "Host Grotesk, sans-serif",
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0f766e",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0f766e",
                    },
                  }}
                >
                  {platformsInfo.map(({ platform }) => (
                    <MenuItem key={platform} value={platform}>
                      <Checkbox
                        checked={selectedPlatforms.indexOf(platform) > -1}
                        size="small"
                        sx={{
                          color: "#0f766e",
                          "&.Mui-checked": { color: "#0f766e" },
                        }}
                      />
                      <ListItemText
                        primary={platform.charAt(0).toUpperCase() + platform.slice(1)}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontFamily: "Host Grotesk, sans-serif",
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Search Query */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "600",
                  color: "#334155",
                  mb: 1,
                  fontFamily: "Host Grotesk, sans-serif",
                }}
              >
                Search Posts
              </Typography>
              <Box
                component="textarea"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter search query..."
                sx={{
                  width: "100%",
                  minHeight: "80px",
                  height: "100px",
                  maxHeight: "240px",
                  p: 2,
                  fontFamily: "Host Grotesk, sans-serif",
                  fontSize: "0.875rem",
                  color: "#334155",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  resize: "vertical",
                  outline: "none",
                  transition: "all 0.2s ease",
                  "&:focus": {
                    borderColor: "#0f766e",
                    boxShadow: "0 0 0 3px rgba(15, 118, 110, 0.1)",
                  },
                  "&::placeholder": {
                    color: "#94a3b8",
                  },
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}
            >
              <Button
                variant="contained"
                onClick={onApplyFilters}
                sx={{
                  backgroundColor: "#0f766e",
                  color: "white",
                  fontFamily: "Host Grotesk, sans-serif",
                  fontWeight: "600",
                  textTransform: "none",
                  py: 1.5,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(15, 118, 110, 0.2)",
                  "&:hover": {
                    backgroundColor: "#0d5b57",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(15, 118, 110, 0.3)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                onClick={onClearFilters}
                sx={{
                  borderColor: "#e2e8f0",
                  color: "#64748b",
                  fontFamily: "Host Grotesk, sans-serif",
                  fontWeight: "600",
                  textTransform: "none",
                  py: 1.5,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: "#cbd5e1",
                    backgroundColor: "#f8fafc",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        )}

        {/* Reports Tab */}
        {activeTab === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "#334155",
                fontFamily: "Host Grotesk, sans-serif",
              }}
            >
              Export &amp; Reports
            </Typography>

            {/* Mode Toggle — Custom Date only shown when records are available */}
            <Box
              sx={{
                display: "flex",
                backgroundColor: "#f1f5f9",
                borderRadius: "999px",
                p: "4px",
                gap: "3px",
              }}
            >
              {[
                { value: "batch", label: "Select Batch", show: true },
                {
                  value: "custom",
                  label: "Custom Date",
                  show: Boolean(recordsMinDate && recordsMaxDate),
                },
              ]
                .filter((m) => m.show)
                .map(({ value, label }) => (
                  <Box
                    key={value}
                    onClick={() => {
                      if (value === "custom") onSelectBatch(null); // deselect batch
                      setReportMode(value);
                    }}
                    sx={{
                      flex: 1,
                      py: 0.75,
                      borderRadius: "999px",
                      cursor: "pointer",
                      textAlign: "center",
                      fontFamily: "Host Grotesk, sans-serif",
                      fontSize: "0.78rem",
                      fontWeight: reportMode === value ? 700 : 500,
                      color: reportMode === value ? "#0f766e" : "#64748b",
                      backgroundColor:
                        reportMode === value ? "#ffffff" : "transparent",
                      border:
                        reportMode === value
                          ? "1.5px solid #ccfbf1"
                          : "1.5px solid transparent",
                      boxShadow:
                        reportMode === value
                          ? "0 1px 4px rgba(0,0,0,0.08)"
                          : "none",
                      transition: "all 0.2s ease",
                      userSelect: "none",
                    }}
                  >
                    {label}
                  </Box>
                ))}
            </Box>

            {/* Batch Mode: Selected Batch Info */}
            {reportMode === "batch" && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: selectedBatch ? "#f0fdfa" : "#ffffff",
                  borderRadius: "8px",
                  border: selectedBatch
                    ? "1.5px solid #0f766e"
                    : "1px solid #e2e8f0",
                  transition: "all 0.2s ease",
                  minHeight: 72,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                {selectedBatch ? (
                  <>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        fontFamily: "Host Grotesk, sans-serif",
                        display: "block",
                        mb: 0.75,
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        fontWeight: 600,
                      }}
                    >
                      Selected Batch
                    </Typography>

                    {/* Start / End datetime */}
                    <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#94a3b8",
                            fontFamily: "Host Grotesk, sans-serif",
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                          }}
                        >
                          Start
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: "#334155",
                            fontFamily: "Host Grotesk, sans-serif",
                            fontSize: "0.82rem",
                          }}
                        >
                          {dayjs(selectedBatch.start_date).format("DD MMM YYYY, HH:mm")}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#94a3b8",
                            fontFamily: "Host Grotesk, sans-serif",
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                          }}
                        >
                          End
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: "#334155",
                            fontFamily: "Host Grotesk, sans-serif",
                            fontSize: "0.82rem",
                          }}
                        >
                          {dayjs(selectedBatch.end_date).format("DD MMM YYYY, HH:mm")}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#0f766e",
                          fontWeight: 600,
                          fontFamily: "Host Grotesk, sans-serif",
                        }}
                      >
                        {selectedBatch.post_count} posts
                      </Typography>
                      <Chip
                        label={selectedBatch.summary_status}
                        size="small"
                        sx={{
                          backgroundColor:
                            selectedBatch.summary_status === "completed"
                              ? "#dcfce7"
                              : "#fef3c7",
                          color:
                            selectedBatch.summary_status === "completed"
                              ? "#15803d"
                              : "#d97706",
                          fontFamily: "Host Grotesk, sans-serif",
                          fontWeight: 600,
                          fontSize: "0.62rem",
                          height: 18,
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#94a3b8",
                      fontFamily: "Host Grotesk, sans-serif",
                      textAlign: "center",
                      fontSize: "0.82rem",
                    }}
                  >
                    Select a Records from the left panel
                  </Typography>
                )}
              </Box>
            )}

            {/* Custom Date & Time Mode */}
            {reportMode === "custom" && (
              <Box>
                <DateTimeRangePicker
                  fromDateTime={customStartDate}
                  setFromDateTime={(val) => {
                    setCustomStartDate(val);
                    onSelectBatch(null); // deselect batch when custom date is changed
                  }}
                  toDateTime={customEndDate}
                  setToDateTime={(val) => {
                    setCustomEndDate(val);
                    onSelectBatch(null);
                  }}
                  minDate={recordsMinDate}
                  maxDate={recordsMaxDate}
                  onClear={() => {
                    setCustomStartDate("");
                    setCustomEndDate("");
                  }}
                />
                {recordsMinDate && recordsMaxDate && (
                  <Box
                    sx={{
                      mt: 1.5,
                      px: 1.5,
                      py: 1,
                      backgroundColor: "#f0fdfa",
                      border: "1px solid #99f6e4",
                      borderRadius: "8px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#0f766e",
                        fontFamily: "Host Grotesk, sans-serif",
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Available range
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#0d5b57",
                        fontFamily: "Host Grotesk, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    >
                      {recordsMinDate.format("DD MMM YYYY, HH:mm")} —{" "}
                      {recordsMaxDate.format("DD MMM YYYY, HH:mm")}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Format Selector */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "#64748b",
                  fontFamily: "Host Grotesk, sans-serif",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontSize: "0.7rem",
                }}
              >
                Select Format
              </Typography>

              {formats.map((format) => (
                <Box
                  key={format.value}
                  onClick={() => setSelectedFormat(format.value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: "8px",
                    border:
                      selectedFormat === format.value
                        ? `2px solid ${format.color}`
                        : "1px solid #e2e8f0",
                    backgroundColor:
                      selectedFormat === format.value
                        ? format.bgColor
                        : "#ffffff",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: format.color,
                      backgroundColor: format.bgColor,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "6px",
                      backgroundColor:
                        selectedFormat === format.value
                          ? format.color
                          : format.bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1rem",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <span
                      style={{
                        color:
                          selectedFormat === format.value ? "#fff" : "inherit",
                        fontSize: "0.9rem",
                      }}
                    >
                      {format.icon}
                    </span>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontFamily: "Host Grotesk, sans-serif",
                        fontSize: "0.85rem",
                      }}
                    >
                      {format.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#94a3b8",
                        fontFamily: "Host Grotesk, sans-serif",
                        fontSize: "0.7rem",
                      }}
                    >
                      {format.subtitle}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border:
                        selectedFormat === format.value
                          ? `5px solid ${format.color}`
                          : "2px solid #cbd5e1",
                      transition: "all 0.15s ease",
                    }}
                  />
                </Box>
              ))}
            </Box>

            {/* Generate Button */}
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              disabled={reportLoading}
              startIcon={
                reportLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
              sx={{
                backgroundColor: "#0f766e",
                color: "white",
                fontFamily: "Host Grotesk, sans-serif",
                fontWeight: 600,
                textTransform: "none",
                py: 1.5,
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(15, 118, 110, 0.2)",
                "&:hover": {
                  backgroundColor: "#0d5b57",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(15, 118, 110, 0.3)",
                },
                "&:disabled": {
                  backgroundColor: "#94a3b8",
                  color: "white",
                  transform: "none",
                  boxShadow: "none",
                },
                transition: "all 0.2s ease",
              }}
            >
              {reportLoading ? "Generating..." : "Generate Report"}
            </Button>
          </Box>
        )}

      </Box>
    </Box>
  );

};

export default DrawerSidebar;
